import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useGuestPrompt } from '../context/GuestPromptContext';
import { isGuest } from '../lib/guestMode';
import { useUI } from '../context/UIContext';
import { useTutorial, TUTORIAL_STEPS } from '../context/TutorialContext';
import { useProfile } from '../context/ProfileContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { supabase } from '../lib/supabase';
import { Capacitor } from '@capacitor/core';
import { sendTradeRequestNotification, sendMultiTradeRequestNotification, sendReactionNotification, sendNewFollowerNotification, notifyFollowers } from '../lib/notifications';
import { setCardNavigationContext } from '../lib/cardNavigationContext';
import Search from '../components/Search';
import LocationFilter from '../components/LocationFilter';
import TutorialOverlay, { TutorialBackdrop } from '../components/TutorialOverlay';
import PushNotificationPrompt from '../components/PushNotificationPrompt';
import AddParentModal from '../components/AddParentModal';
import ChaseWaveImage from '../assets/images/ChaseWave.png';
import ChaseThumbsUpImage from '../assets/images/ChaseThumbsUp.png';
import CardImage from '../components/CardImage';

// Trade Button Arrows Component - shows arrows pointing to Trade buttons during tutorial
const TradeButtonArrows = ({ visible }) => {
  const { theme } = useTheme();
  const [arrows, setArrows] = useState([]);

  useEffect(() => {
    if (!visible) {
      setArrows([]);
      return;
    }

    const updateArrows = () => {
      const buttons = document.querySelectorAll('[data-tutorial="trade-btn"]');
      const newArrows = [];

      buttons.forEach((btn, idx) => {
        const rect = btn.getBoundingClientRect();
        // Only show arrows for buttons visible in viewport
        if (rect.top > 100 && rect.bottom < window.innerHeight - 100) {
          newArrows.push({
            id: idx,
            x: rect.left + rect.width / 2,
            y: rect.top - 10,
          });
        }
      });

      setArrows(newArrows);
    };

    // Initial update after a brief delay to ensure DOM is ready
    setTimeout(updateArrows, 100);

    // Update frequently with interval since scroll events can be tricky
    const interval = setInterval(updateArrows, 200);

    // Also listen to scroll on window and any scrollable container
    window.addEventListener('scroll', updateArrows, true);
    window.addEventListener('resize', updateArrows);

    return () => {
      clearInterval(interval);
      window.removeEventListener('scroll', updateArrows, true);
      window.removeEventListener('resize', updateArrows);
    };
  }, [visible]);

  if (!visible || arrows.length === 0) return null;

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, pointerEvents: 'none', zIndex: 9998 }}>
      {arrows.map(arrow => (
        <div
          key={arrow.id}
          style={{
            position: 'absolute',
            left: arrow.x,
            top: arrow.y,
            transform: 'translate(-50%, -100%)',
          }}
        >
          <svg width="40" height="40" viewBox="0 0 40 40" style={{ animation: 'bounceDown 1s infinite' }}>
            <path
              d="M20 10 L20 30 M20 30 L12 22 M20 30 L28 22"
              stroke={theme.error}
              strokeWidth="4"
              strokeLinecap="round"
              fill="none"
            />
          </svg>
        </div>
      ))}
      <style>{`
        @keyframes bounceDown {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(8px); }
        }
      `}</style>
    </div>
  );
};

// Rating Ball System Component
const RatingBallSystem = ({ postId, ratings = {}, selectedRating, onRatingSelect, isMultiCard = false, isScrollIdle = true }) => {
  const { theme } = useTheme();
  const [showText, setShowText] = useState(false);
  const promptText = isMultiCard ? 'Rate these cards' : 'Rate this card';
  const idleTimerRef = useRef(null);
  const textTimerRef = useRef(null);

  const ballVariants = {
    poke: { top: '#FFADAD', selected: '#FF3B30', badge: '#FF3B30' },
    great: { top: '#ADE8FF', selected: '#007AFF', badge: '#007AFF' },
    ultra: { top: '#FFC107', selected: '#FFC107', badge: '#FFC107' },
    master: { top: '#E2C3F9', selected: '#AF52DE', badge: '#AF52DE' },
  };

  // Balls visible by default. When idle for 3s, briefly show text, then back to balls
  useEffect(() => {
    // Always show balls if already rated
    if (selectedRating) {
      setShowText(false);
      return;
    }

    // User is scrolling - clear timers and show balls
    if (!isScrollIdle) {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      if (textTimerRef.current) clearTimeout(textTimerRef.current);
      setShowText(false);
      return;
    }

    // After 3 seconds of idle, show text briefly
    idleTimerRef.current = setTimeout(() => {
      setShowText(true);

      // After 2.5 seconds of showing text, go back to balls
      textTimerRef.current = setTimeout(() => {
        setShowText(false);
      }, 2500);
    }, 3000);

    return () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      if (textTimerRef.current) clearTimeout(textTimerRef.current);
    };
  }, [isScrollIdle, selectedRating]);

  const handleRating = (type) => {
    onRatingSelect(postId, type);
  };

  const renderBall = (type) => {
    const isSelected = selectedRating === type;
    const count = ratings[type] || 0;
    const colors = ballVariants[type];

    return (
      <button
        key={type}
        style={styles(theme).ratingBallButton}
        onClick={() => handleRating(type)}
        aria-label={`Rate with ${type} ball`}
      >
        <div style={{
          ...styles(theme).ratingBall,
          ...(isSelected ? styles(theme).ratingBallSelected : {}),
        }}>
          {/* Top half */}
          <div style={{
            ...styles(theme).ratingBallTop,
            backgroundColor: isSelected ? colors.selected : colors.top,
          }} />
          {/* Bottom half */}
          <div style={{
            ...styles(theme).ratingBallBottom,
            ...(isSelected ? styles(theme).ratingBallBottomSelected : {}),
          }} />
          {/* Center divider */}
          <div style={{
            ...styles(theme).ratingBallDivider,
            ...(isSelected ? styles(theme).ratingBallDividerSelected : {}),
          }} />
        </div>
        {/* Count badge */}
        {count > 0 && (
          <div style={{
            ...styles(theme).ratingCountBadge,
            backgroundColor: colors.badge,
          }}>
            <span style={styles(theme).ratingCountText}>{count}</span>
          </div>
        )}
      </button>
    );
  };

  return (
    <div style={styles(theme).ratingBallSystem}>
      {/* Text prompt - shows briefly when idle */}
      {showText && !selectedRating ? (
        <div style={styles(theme).ratingPromptContainer}>
          <span style={styles(theme).ratingPromptText}>{promptText}</span>
        </div>
      ) : (
        /* Rating balls - visible by default */
        <div style={styles(theme).ratingBallsContainer}>
          {['poke', 'great', 'ultra', 'master'].map(renderBall)}
        </div>
      )}
    </div>
  );
};

// Skeleton Post Component
const SkeletonPost = () => {
  const { theme } = useTheme();
  return (
    <div style={styles(theme).skeletonPost}>
      {/* User row skeleton */}
      <div style={styles(theme).skeletonUserRow}>
        <div style={styles(theme).skeletonUsername} />
        <div style={styles(theme).skeletonFollowBtn} />
      </div>
      {/* Post type banner skeleton */}
      <div style={styles(theme).skeletonBanner} />
      {/* Card image skeleton */}
      <div style={styles(theme).skeletonCard} />
      {/* Details skeleton */}
      <div style={styles(theme).skeletonDetails}>
        <div style={styles(theme).skeletonTimestamp} />
        <div style={styles(theme).skeletonLevel} />
      </div>
      {/* Rating skeleton */}
      <div style={styles(theme).skeletonRating} />
    </div>
  );
};

// Helper to format distance display
const formatDistance = (distance) => {
  if (distance === null || distance === undefined) return null;
  if (distance < 5) return '<5 mi';
  if (distance < 10) return '<10 mi';
  return `${distance} mi`;
};

// Single Card Post Component
const SingleCardPost = ({ post, viewMode, currentUserId, currentUserProfile, onToggleFollow, onReport, onBlock, onDeletePost, userRatings, onRatingSelect, onNavigate, isScrollIdle, requestedCardIds, onSendTradeRequest }) => {
  const { theme } = useTheme();
  const defaultNavigate = useNavigate();
  const navigate = onNavigate || defaultNavigate;
  const [menuOpen, setMenuOpen] = useState(false);

  const card = post.card;
  const canTrade = card.trading_status === 'TRADE';
  const isOwnPost = currentUserId === post.user.id;
  const isRequested = requestedCardIds?.has(card.id);

  // Calculate distance from current user
  const userDistance = post.distance !== undefined ? post.distance : null;

  // Count available photos
  const photoCount = [
    card.digitalImage,
    card.originalImage,
    card.backPhoto,
    card.frontTopPhoto,
    card.frontBottomPhoto,
    card.backTopPhoto,
    card.backBottomPhoto,
  ].filter(Boolean).length;

  // Navigate to card detail with context (single card context)
  const navigateToCard = (suffix = '') => {
    setCardNavigationContext({
      type: 'home-post',
      cardIds: [card.id],
      currentIndex: 0,
      sourceId: post.id,
      sourceLabel: `${post.user.username}'s ${post.postType}`,
    });
    navigate(`/card/${card.id}${suffix}`);
  };

  // Action handlers
  const handleZoom = () => {
    if (card.id) navigateToCard('?view=zoom&photo=0');
  };

  const handleBackPhoto = () => {
    if (card.id) navigateToCard('?view=zoom&photo=back');
  };

  const handleGallery = () => {
    if (card.id) navigateToCard('?view=grid');
  };

  const handleFullBinder = () => {
    // Navigate to user's collection with binder param to open that binder
    if (post.user.id && card.binderId) {
      navigate(`/user/${post.user.id}/collection?binder=${card.binderId}`);
    }
  };

  const handleCardClick = () => {
    if (card.id) navigateToCard('?view=zoom&photo=0');
  };

  const handleDetails = () => {
    if (card.id) navigateToCard();
  };

  // Level colors matching native app
  const getLevelColor = (level) => {
    const colors = {
      0: theme.textSecondary,
      1: '#059669',
      2: '#0284c7',
      3: '#7c3aed',
      4: '#dc2626',
      5: '#ea580c',
      6: '#fbbf24',
      7: '#ec4899',
      8: '#1f2937',
    };
    return colors[level] || colors[1];
  };

  const handleUserPress = () => {
    if (post.user.id) {
      navigate(`/user/${post.user.id}/collection`);
    }
  };

  return (
    <div style={styles(theme).postContainer}>
      {/* User Info Row - now includes timestamp and level */}
      <div style={styles(theme).userRow}>
        <div style={styles(theme).userInfoLeft}>
          <button style={styles(theme).usernameButton} onClick={handleUserPress}>
            <span style={styles(theme).username}>{post.user.username}</span>
          </button>
          <span style={styles(theme).userMeta}>
            {post.timestamp}
            <span style={styles(theme).metaDot}></span>
            <span style={{
              ...styles(theme).levelBadge,
              backgroundColor: getLevelColor(post.user.tradeLevel),
            }}>Lvl {post.user.tradeLevel}</span>
            {userDistance !== null && (
              <>
                <span style={styles(theme).metaDot}></span>
                <span style={styles(theme).distanceBadge}>{formatDistance(userDistance)}</span>
              </>
            )}
          </span>
        </div>
        <div style={styles(theme).menuWrapper}>
          <button
            style={styles(theme).moreButton}
            onClick={() => setMenuOpen(true)}
          >
            <MoreVerticalIcon />
          </button>
          <PostOptionsMenu
            isOpen={menuOpen}
            onClose={() => setMenuOpen(false)}
            user={post.user}
            isFollowing={post.user.followed}
            onToggleFollow={() => onToggleFollow(post.user.id)}
            onReport={() => onReport(post)}
            onBlock={() => onBlock(post.user.id)}
            isOwnPost={isOwnPost}
            onDeletePost={() => onDeletePost(post)}
          />
        </div>
      </div>

      {/* Hero Container */}
      <div style={styles(theme).heroContainer}>
        {/* Post Type Banner */}
        <div style={styles(theme).postTypeBanner}>
          <span style={styles(theme).postTypeText}>{post.postType}</span>
        </div>

        {/* Card Image - Single Card */}
        <div
          style={{ ...styles(theme).cardImageContainer, cursor: 'pointer' }}
          onClick={handleCardClick}
        >
          {/* Shine Effect */}
          <div style={styles(theme).heroShine} />

          <CardImage
            digitalImage={card.digitalImage}
            userPhoto={card.originalImage}
            name={card.name}
            number={card.number}
            setName={card.set}
            preferPhoto={viewMode === 'photo'}
            imgStyle={styles(theme).cardImage}
            theme={theme}
            loading="eager"
          />

          {/* Card Info Overlay */}
          <div style={styles(theme).cardInfoOverlay}>
            <span style={styles(theme).cardName}>{card.name}</span>
            <div style={styles(theme).cardInfoRow}>
              <span style={styles(theme).gradeChip}>{card.grade}</span>
              <span style={{
                ...styles(theme).tradingStatusChip,
                backgroundColor: canTrade ? 'rgba(34, 197, 94, 0.2)' : 'rgba(107, 114, 128, 0.2)',
                color: canTrade ? theme.success : theme.textSecondary,
              }}>
                {canTrade ? 'For Trade' : 'PC'}
              </span>
            </div>
          </div>
        </div>

        {/* Action Row */}
        <div style={styles(theme).actionRow}>
          <button style={styles(theme).actionLink} onClick={handleDetails}>
            Details
          </button>
          {card.backPhoto && (
            <button style={styles(theme).actionLink} onClick={handleBackPhoto}>
              Back
            </button>
          )}
          {photoCount > 1 && (
            <button style={styles(theme).actionLink} onClick={handleGallery}>
              Gallery ({photoCount})
            </button>
          )}
          {card.binderId && (
            <button style={styles(theme).actionLink} onClick={handleFullBinder}>
              Full Binder
            </button>
          )}
        </div>
      </div>

      {/* Rating Row: Rating (left) | Trade (right) */}
      <div style={styles(theme).ratingRow}>
        <RatingBallSystem
          postId={post.id}
          ratings={post.ratings || { poke: 0, great: 0, ultra: 0, master: 0 }}
          selectedRating={userRatings?.[post.id]}
          onRatingSelect={onRatingSelect}
          isMultiCard={false}
          isScrollIdle={isScrollIdle}
        />
        {!isOwnPost && canTrade && (
          isRequested ? (
            <div style={styles(theme).tradeLinkRequested}>
              <CheckIcon />
              <span>Requested</span>
            </div>
          ) : (
            <button
              style={styles(theme).tradeLink}
              onClick={() => onSendTradeRequest(card.id, post.user.id, card.name, card.digitalImage || card.originalImage, post.user.username)}
              data-tutorial="trade-btn"
            >
              Trade
            </button>
          )
        )}
        {!isOwnPost && !canTrade && (
          <span style={styles(theme).tradeLinkDisabled}>PC</span>
        )}
      </div>
    </div>
  );
};

// Multi Card Post Component - Thumbnail Strip View
const MultiCardPost = ({ post, viewMode, currentUserId, currentUserProfile, onToggleFollow, onReport, onBlock, onDeletePost, userRatings, onRatingSelect, onNavigate, isScrollIdle, requestedCardIds, onSendTradeRequest, onMultiCardTradeClick }) => {
  const { theme } = useTheme();
  const defaultNavigate = useNavigate();
  const navigate = onNavigate || defaultNavigate;
  const [selectedIndex, setSelectedIndex] = useState(null); // null = show all thumbnails
  const [menuOpen, setMenuOpen] = useState(false);
  const isOwnPost = currentUserId === post.user.id;

  const cards = post.cards || [];

  // Navigate to card detail with context for left/right navigation
  const navigateToCard = (cardId, suffix = '') => {
    const cardIds = cards.map(c => c.id);
    const currentIndex = cardIds.indexOf(cardId);
    setCardNavigationContext({
      type: 'home-post',
      cardIds,
      currentIndex: currentIndex >= 0 ? currentIndex : 0,
      sourceId: post.id,
      sourceLabel: `${post.user.username}'s ${post.postType}`,
    });
    navigate(`/card/${cardId}${suffix}`);
  };
  // Check if any card in this post is already requested
  const anyCardRequested = cards.some(c => requestedCardIds?.has(c.id));
  // Get the first tradeable card for the trade button
  const firstTradeableCard = cards.find(c => c.trading_status === 'TRADE');
  const hasTradeableCards = !!firstTradeableCard;
  const selectedCard = selectedIndex !== null ? cards[selectedIndex] : null;

  // Calculate distance from current user
  const userDistance = post.distance !== undefined ? post.distance : null;

  const getLevelColor = (level) => {
    const colors = {
      0: theme.textSecondary, 1: '#059669', 2: '#0284c7', 3: '#7c3aed',
      4: '#dc2626', 5: '#ea580c', 6: '#fbbf24', 7: '#ec4899', 8: '#1f2937',
    };
    return colors[level] || colors[1];
  };

  const handleUserPress = () => {
    if (post.user.id) {
      navigate(`/user/${post.user.id}/collection`);
    }
  };

  const selectCard = (index) => {
    setSelectedIndex(index);
  };

  const goBack = () => {
    setSelectedIndex(null);
  };

  const goNext = () => {
    if (selectedIndex !== null) {
      setSelectedIndex((prev) => (prev + 1) % cards.length);
    }
  };

  const goPrev = () => {
    if (selectedIndex !== null) {
      setSelectedIndex((prev) => (prev - 1 + cards.length) % cards.length);
    }
  };

  return (
    <div style={styles(theme).postContainer}>
      {/* User Info Row - includes timestamp and level */}
      <div style={styles(theme).userRow}>
        <div style={styles(theme).userInfoLeft}>
          <button style={styles(theme).usernameButton} onClick={handleUserPress}>
            <span style={styles(theme).username}>{post.user.username}</span>
          </button>
          <span style={styles(theme).userMeta}>
            {post.timestamp}
            <span style={styles(theme).metaDot}></span>
            <span style={{
              ...styles(theme).levelBadge,
              backgroundColor: getLevelColor(post.user.tradeLevel),
            }}>Lvl {post.user.tradeLevel}</span>
            {userDistance !== null && (
              <>
                <span style={styles(theme).metaDot}></span>
                <span style={styles(theme).distanceBadge}>{formatDistance(userDistance)}</span>
              </>
            )}
          </span>
        </div>
        <div style={styles(theme).menuWrapper}>
          <button
            style={styles(theme).moreButton}
            onClick={() => setMenuOpen(true)}
          >
            <MoreVerticalIcon />
          </button>
          <PostOptionsMenu
            isOpen={menuOpen}
            onClose={() => setMenuOpen(false)}
            user={post.user}
            isFollowing={post.user.followed}
            onToggleFollow={() => onToggleFollow(post.user.id)}
            onReport={() => onReport(post)}
            onBlock={() => onBlock(post.user.id)}
            isOwnPost={isOwnPost}
            onDeletePost={() => onDeletePost(post)}
          />
        </div>
      </div>

      {/* Hero Container */}
      <div style={styles(theme).heroContainer}>
        {/* Post Type Banner */}
        <div style={styles(theme).postTypeBanner}>
          <span style={styles(theme).postTypeText}>{post.postType}</span>
        </div>

        {selectedIndex === null ? (
          // Thumbnail Grid View
          <>
            <div style={styles(theme).thumbnailGrid}>
              {cards.map((card, index) => (
                <button
                  key={index}
                  style={styles(theme).thumbnailCard}
                  onClick={() => selectCard(index)}
                >
                  <CardImage
                    digitalImage={card.digitalImage}
                    userPhoto={card.originalImage}
                    name={card.name}
                    number={card.number}
                    setName={card.set}
                    preferPhoto={viewMode === 'photo'}
                    imgStyle={styles(theme).thumbnailImage}
                    theme={theme}
                    loading="lazy"
                  />
                </button>
              ))}
            </div>
            <div style={styles(theme).multiCardHint}>
              <span style={styles(theme).multiCardHintText}>Tap a card to view</span>
            </div>
          </>
        ) : (
          // Expanded Single Card View
          <>
            {/* Back button */}
            <button style={styles(theme).backToGridBtn} onClick={goBack}>
              <ChevronLeftIcon /> <span>All {cards.length} cards</span>
            </button>

            {/* Card Image with Navigation */}
            <div style={styles(theme).cardImageContainer}>
              {/* Shine Effect */}
              <div style={styles(theme).heroShine} />

              {/* Prev Arrow */}
              <button style={styles(theme).navArrowLeft} onClick={goPrev}>
                <ChevronLeftIcon />
              </button>

              {(() => {
                const canTrade = selectedCard.trading_status === 'TRADE';
                return (
                  <>
                    <CardImage
                      digitalImage={selectedCard.digitalImage}
                      userPhoto={selectedCard.originalImage}
                      name={selectedCard.name}
                      number={selectedCard.number}
                      setName={selectedCard.set}
                      preferPhoto={viewMode === 'photo'}
                      imgStyle={{
                        ...styles(theme).cardImage,
                        cursor: 'pointer',
                      }}
                      theme={theme}
                      loading="eager"
                      onClick={() => selectedCard.id && navigateToCard(selectedCard.id, '?view=zoom&photo=0')}
                    />
                    {/* Card Info Overlay */}
                    <div style={styles(theme).cardInfoOverlay}>
                      <span style={styles(theme).cardName}>{selectedCard.name}</span>
                      <div style={styles(theme).cardInfoRow}>
                        <span style={styles(theme).gradeChip}>{selectedCard.grade}</span>
                        <span style={{
                          ...styles(theme).tradingStatusChip,
                          backgroundColor: canTrade ? 'rgba(34, 197, 94, 0.2)' : 'rgba(107, 114, 128, 0.2)',
                          color: canTrade ? theme.success : theme.textSecondary,
                        }}>
                          {canTrade ? 'For Trade' : 'PC'}
                        </span>
                      </div>
                    </div>
                  </>
                );
              })()}

              {/* Next Arrow */}
              <button style={styles(theme).navArrowRight} onClick={goNext}>
                <ChevronRightIcon />
              </button>
            </div>

            {/* Pagination Dots */}
            <div style={styles(theme).paginationDots}>
              {cards.map((_, index) => (
                <button
                  key={index}
                  style={{
                    ...styles(theme).paginationDot,
                    backgroundColor: index === selectedIndex ? theme.accent : theme.border,
                  }}
                  onClick={() => selectCard(index)}
                />
              ))}
            </div>

            {/* Action Row for selected card */}
            {selectedCard && (
              <div style={styles(theme).actionRow}>
                <button style={styles(theme).actionLink} onClick={() => navigateToCard(selectedCard.id)}>
                  Details
                </button>
                {selectedCard.backPhoto && (
                  <button style={styles(theme).actionLink} onClick={() => navigateToCard(selectedCard.id, '?view=zoom&photo=back')}>
                    Back
                  </button>
                )}
                {(selectedCard.photos?.length > 1 || selectedCard.backPhoto) && (
                  <button style={styles(theme).actionLink} onClick={() => navigateToCard(selectedCard.id, '?view=zoom&photo=0')}>
                    Gallery ({(selectedCard.photos?.length || 1) + (selectedCard.backPhoto ? 1 : 0)})
                  </button>
                )}
                {selectedCard.binderId && (
                  <button style={styles(theme).actionLink} onClick={() => navigate(`/user/${post.user.id}/collection?binder=${selectedCard.binderId}`)}>
                    Full Binder
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Rating Row: Rating (left) | Trade (right) */}
      <div style={styles(theme).ratingRow}>
        <RatingBallSystem
          postId={post.id}
          ratings={post.ratings || { poke: 0, great: 0, ultra: 0, master: 0 }}
          selectedRating={userRatings?.[post.id]}
          onRatingSelect={onRatingSelect}
          isMultiCard={true}
          isScrollIdle={isScrollIdle}
        />
        {!isOwnPost && hasTradeableCards && (
          anyCardRequested ? (
            <div style={styles(theme).tradeLinkRequested}>
              <CheckIcon />
              <span>Requested</span>
            </div>
          ) : (
            <button
              style={styles(theme).tradeLink}
              onClick={() => onMultiCardTradeClick ? onMultiCardTradeClick(post) : onSendTradeRequest(firstTradeableCard.id, post.user.id, firstTradeableCard.name)}
              data-tutorial="trade-btn"
            >
              Trade
            </button>
          )
        )}
        {!isOwnPost && !hasTradeableCards && (
          <span style={styles(theme).tradeLinkDisabled}>PC</span>
        )}
      </div>
    </div>
  );
};

// Arrow icons for multi-card navigation
const ChevronLeftIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m15 18-6-6 6-6" />
  </svg>
);

const ChevronRightIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m9 18 6-6-6-6" />
  </svg>
);

// Simple icon components (will replace with lucide-react later)
const UserIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
    <circle cx="12" cy="7" r="4"></circle>
  </svg>
);

const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8"></circle>
    <path d="m21 21-4.35-4.35"></path>
  </svg>
);

const InfoIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"></circle>
    <path d="M12 16v-4"></path>
    <path d="M12 8h.01"></path>
  </svg>
);

const FilterIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
  </svg>
);

const MapPinIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

const UsersIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const RepeatIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m17 2 4 4-4 4"></path>
    <path d="M3 11v-1a4 4 0 0 1 4-4h14"></path>
    <path d="m7 22-4-4 4-4"></path>
    <path d="M21 13v1a4 4 0 0 1-4 4H3"></path>
  </svg>
);

const XIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 6 6 18"></path>
    <path d="m6 6 12 12"></path>
  </svg>
);

const MoreVerticalIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="5" r="1.5"></circle>
    <circle cx="12" cy="12" r="1.5"></circle>
    <circle cx="12" cy="19" r="1.5"></circle>
  </svg>
);

const CheckIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 6L9 17l-5-5" />
  </svg>
);

// Post Options Menu Component
const PostOptionsMenu = ({ isOpen, onClose, user, isFollowing, onToggleFollow, onReport, onBlock, isOwnPost, onDeletePost }) => {
  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div style={menuStyles.backdrop} onClick={onClose} />
      {/* Menu */}
      <div style={menuStyles.menu}>
        {isOwnPost && onDeletePost && (
          <>
            <button style={menuStyles.menuItemDanger} onClick={() => { onDeletePost(); onClose(); }}>
              <span style={menuStyles.menuItemTextDanger}>Delete Post</span>
            </button>
          </>
        )}
        {!isOwnPost && (
          <>
            <button style={menuStyles.menuItem} onClick={() => { onToggleFollow(); onClose(); }}>
              <span style={menuStyles.menuItemText}>
                {isFollowing ? 'Unfollow' : 'Follow'}
              </span>
            </button>
            <div style={menuStyles.menuDivider} />
            <button style={menuStyles.menuItem} onClick={() => { onBlock(); onClose(); }}>
              <span style={menuStyles.menuItemText}>Block User</span>
            </button>
            <div style={menuStyles.menuDivider} />
            <button style={menuStyles.menuItemDanger} onClick={() => { onReport(); onClose(); }}>
              <span style={menuStyles.menuItemTextDanger}>Report</span>
            </button>
          </>
        )}
      </div>
    </>
  );
};

// Report Modal Component
const ReportModal = ({ isOpen, onClose, post, currentUserId, onSuccess }) => {
  const [selectedReason, setSelectedReason] = useState(null);
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const reasons = [
    { id: 'nsfw', label: 'NSFW Content', description: 'Adult or inappropriate content' },
    { id: 'wrong_content', label: 'Wrong Content', description: 'Not Pokemon/TCG related' },
    { id: 'spam', label: 'Spam', description: 'Repetitive or promotional content' },
    { id: 'scam', label: 'Scam', description: 'Fraudulent or deceptive content' },
    { id: 'other', label: 'Other', description: 'Another issue not listed' },
  ];

  const handleSubmit = async () => {
    if (!selectedReason || !currentUserId) return;

    setSubmitting(true);
    try {
      const reportData = {
        reporter_id: currentUserId,
        reason: selectedReason,
        description: description.trim() || null,
        status: 'pending',
      };

      // Report the card if available, otherwise report the user
      if (post.card?.id) {
        reportData.reported_card_id = post.card.id;
      } else if (post.user?.id) {
        reportData.reported_user_id = post.user.id;
      }

      const { error } = await supabase
        .from('reports')
        .insert(reportData);

      if (error) {
        if (error.code === '23505') {
          alert('You have already reported this content.');
        } else {
          throw error;
        }
      } else {
        // Hide the card immediately for safety (admin will review)
        if (post.card?.id) {
          await supabase
            .from('cards')
            .update({ hidden: true })
            .eq('id', post.card.id);
        }

        onSuccess && onSuccess(post);
        onClose();
        // Reset state
        setSelectedReason(null);
        setDescription('');
      }
    } catch (err) {
      console.error('Error submitting report:', err);
      alert('Failed to submit report. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={reportStyles.overlay} onClick={onClose}>
      <div style={reportStyles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={reportStyles.header}>
          <h2 style={reportStyles.title}>Report Content</h2>
          <button style={reportStyles.closeButton} onClick={onClose}>
            <XIcon />
          </button>
        </div>

        {/* Content */}
        <div style={reportStyles.content}>
          <p style={reportStyles.subtitle}>Why are you reporting this?</p>

          {/* Reason Options */}
          <div style={reportStyles.reasonsList}>
            {reasons.map((reason) => (
              <button
                key={reason.id}
                style={{
                  ...reportStyles.reasonButton,
                  ...(selectedReason === reason.id ? reportStyles.reasonButtonSelected : {}),
                }}
                onClick={() => setSelectedReason(reason.id)}
              >
                <div style={reportStyles.reasonContent}>
                  <span style={{
                    ...reportStyles.reasonLabel,
                    ...(selectedReason === reason.id ? reportStyles.reasonLabelSelected : {}),
                  }}>{reason.label}</span>
                  <span style={reportStyles.reasonDescription}>{reason.description}</span>
                </div>
                <div style={{
                  ...reportStyles.radioOuter,
                  ...(selectedReason === reason.id ? reportStyles.radioOuterSelected : {}),
                }}>
                  {selectedReason === reason.id && <div style={reportStyles.radioInner} />}
                </div>
              </button>
            ))}
          </div>

          {/* Optional Description */}
          <div style={reportStyles.descriptionSection}>
            <label style={reportStyles.descriptionLabel}>Additional details (optional)</label>
            <textarea
              style={reportStyles.descriptionInput}
              placeholder="Tell us more about the issue..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
            />
          </div>
        </div>

        {/* Footer */}
        <div style={reportStyles.footer}>
          <button style={reportStyles.cancelButton} onClick={onClose}>
            Cancel
          </button>
          <button
            style={{
              ...reportStyles.submitButton,
              ...((!selectedReason || submitting) ? reportStyles.submitButtonDisabled : {}),
            }}
            onClick={handleSubmit}
            disabled={!selectedReason || submitting}
          >
            {submitting ? 'Submitting...' : 'Submit Report'}
          </button>
        </div>
      </div>
    </div>
  );
};

const reportStyles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'var(--overlay)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2000,
    padding: '20px',
  },
  modal: {
    width: '100%',
    maxWidth: '400px',
    backgroundColor: 'var(--bg-sheet)',
    borderRadius: '16px',
    border: '1px solid var(--border)',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 20px',
    borderBottom: '1px solid var(--border)',
  },
  title: {
    color: 'var(--text)',
    fontSize: '18px',
    fontWeight: '600',
    margin: 0,
  },
  closeButton: {
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    backgroundColor: 'var(--bg-card)',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: 'var(--text-secondary)',
  },
  content: {
    padding: '20px',
  },
  subtitle: {
    color: 'var(--text-secondary)',
    fontSize: '14px',
    margin: '0 0 16px 0',
  },
  reasonsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  reasonButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 16px',
    backgroundColor: 'var(--bg-card)',
    border: '1px solid var(--border)',
    borderRadius: '12px',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    textAlign: 'left',
  },
  reasonButtonSelected: {
    backgroundColor: 'var(--accent-bg)',
    borderColor: 'var(--accent)',
  },
  reasonContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  reasonLabel: {
    color: 'var(--text)',
    fontSize: '14px',
    fontWeight: '500',
  },
  reasonLabelSelected: {
    color: 'var(--accent)',
  },
  reasonDescription: {
    color: 'var(--text-secondary)',
    fontSize: '12px',
  },
  radioOuter: {
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    border: '2px solid var(--border)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  radioOuterSelected: {
    borderColor: 'var(--accent)',
  },
  radioInner: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    backgroundColor: 'var(--accent)',
  },
  descriptionSection: {
    marginTop: '20px',
  },
  descriptionLabel: {
    display: 'block',
    color: 'var(--text-secondary)',
    fontSize: '13px',
    marginBottom: '8px',
  },
  descriptionInput: {
    width: '100%',
    padding: '12px',
    backgroundColor: 'var(--bg-card)',
    border: '1px solid var(--border)',
    borderRadius: '10px',
    color: 'var(--text)',
    fontSize: '14px',
    resize: 'none',
    outline: 'none',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
  },
  footer: {
    display: 'flex',
    gap: '12px',
    padding: '16px 20px',
    borderTop: '1px solid var(--border)',
  },
  cancelButton: {
    flex: 1,
    padding: '12px',
    backgroundColor: 'transparent',
    border: '1px solid var(--border)',
    borderRadius: '10px',
    color: 'var(--text-secondary)',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  submitButton: {
    flex: 1,
    padding: '12px',
    backgroundColor: 'var(--error)',
    border: 'none',
    borderRadius: '10px',
    color: '#ffffff',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  submitButtonDisabled: {
    backgroundColor: 'var(--text-secondary)',
    cursor: 'not-allowed',
  },
};

const menuStyles = {
  backdrop: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 999,
  },
  menu: {
    position: 'absolute',
    top: '100%',
    right: 0,
    marginTop: '4px',
    backgroundColor: 'var(--bg-card)',
    border: '1px solid var(--border)',
    borderRadius: '12px',
    overflow: 'hidden',
    minWidth: '140px',
    zIndex: 1000,
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)',
  },
  menuItem: {
    width: '100%',
    padding: '12px 16px',
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    textAlign: 'left',
  },
  menuItemDanger: {
    width: '100%',
    padding: '12px 16px',
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    textAlign: 'left',
  },
  menuItemText: {
    color: 'var(--text)',
    fontSize: '14px',
    fontWeight: '500',
  },
  menuItemTextDanger: {
    color: 'var(--error)',
    fontSize: '14px',
    fontWeight: '500',
  },
  menuDivider: {
    height: '1px',
    backgroundColor: 'var(--border)',
  },
};

// Trade Confirmation Modal - Single card
const TradeConfirmModal = ({ data, onConfirm, onCancel }) => {
  if (!data) return null;

  return (
    <div style={tradeModalStyles.overlay} onClick={onCancel}>
      <div style={tradeModalStyles.content} onClick={(e) => e.stopPropagation()}>
        <h3 style={tradeModalStyles.title}>Send Trade Request?</h3>

        {data.cardImage && (
          <img
            src={data.cardImage}
            alt={data.cardName}
            style={tradeModalStyles.cardImage}
          />
        )}

        <p style={tradeModalStyles.text}>
          Request to trade for <span style={tradeModalStyles.highlight}>{data.cardName}</span> from <span style={tradeModalStyles.highlight}>{data.ownerName}</span>?
        </p>

        <div style={tradeModalStyles.actions}>
          <button style={tradeModalStyles.cancelButton} onClick={onCancel}>
            Cancel
          </button>
          <button style={tradeModalStyles.confirmButton} onClick={onConfirm}>
            Send Request
          </button>
        </div>
      </div>
    </div>
  );
};

// Multi-Card Selection Modal
const MultiCardSelectModal = ({ data, onConfirm, onCancel, requestedCardIds }) => {
  const { theme } = useTheme();
  const [selectedCards, setSelectedCards] = useState(new Set());
  const [toast, setToast] = useState(null);

  if (!data) return null;

  const allCards = data.cards || [];

  const handleCardClick = (card) => {
    const alreadyRequested = requestedCardIds?.has(card.id);
    const isAvailable = card.trading_status === 'TRADE' && !alreadyRequested;

    console.log('[MultiCard] Card clicked:', card.name, 'trading_status:', card.trading_status, 'isAvailable:', isAvailable, 'alreadyRequested:', alreadyRequested);

    if (alreadyRequested) {
      console.log('[MultiCard] Showing toast: already requested');
      setToast("You've already requested this card");
      setTimeout(() => setToast(null), 2000);
      return;
    }

    if (!isAvailable) {
      console.log('[MultiCard] Showing toast: PC');
      setToast("This card is in their Personal Collection");
      setTimeout(() => setToast(null), 2000);
      return;
    }

    setSelectedCards(prev => {
      const newSet = new Set(prev);
      if (newSet.has(card.id)) {
        newSet.delete(card.id);
      } else {
        newSet.add(card.id);
      }
      return newSet;
    });
  };

  const handleConfirm = () => {
    if (selectedCards.size > 0) {
      onConfirm(Array.from(selectedCards));
    }
  };

  const tradeableCount = allCards.filter(c => c.trading_status === 'TRADE' && !requestedCardIds?.has(c.id)).length;

  return (
    <div style={tradeModalStyles.overlay} onClick={onCancel}>
      <div style={tradeModalStyles.multiContent} onClick={(e) => e.stopPropagation()}>
        <h3 style={tradeModalStyles.title}>Select Cards to Trade</h3>
        <p style={tradeModalStyles.subtitle}>
          {tradeableCount > 0
            ? `${tradeableCount} of ${allCards.length} cards available for trade`
            : 'No cards available for trade'}
        </p>

        <div style={tradeModalStyles.cardGrid}>
          {allCards.map((card) => {
            const alreadyRequested = requestedCardIds?.has(card.id);
            const isSelected = selectedCards.has(card.id);
            const isAvailable = card.trading_status === 'TRADE' && !alreadyRequested;

            return (
              <button
                key={card.id}
                style={{
                  ...tradeModalStyles.cardSelectItem,
                  borderColor: isSelected ? theme.accent : theme.border,
                  backgroundColor: isSelected ? 'rgba(14, 165, 233, 0.1)' : theme.bgCard,
                  opacity: isAvailable ? 1 : 0.4,
                }}
                onClick={() => handleCardClick(card)}
              >
                <CardImage
                  digitalImage={card.digitalImage}
                  userPhoto={card.originalImage}
                  name={card.name}
                  imgStyle={tradeModalStyles.cardSelectImage}
                  theme={theme}
                  loading="lazy"
                />
                {isSelected && (
                  <div style={tradeModalStyles.checkBadge}>
                    <CheckIcon />
                  </div>
                )}
                {alreadyRequested && (
                  <div style={tradeModalStyles.requestedBadge}>Requested</div>
                )}
                {!isAvailable && !alreadyRequested && (
                  <div style={tradeModalStyles.pcBadge}>PC</div>
                )}
                <span style={tradeModalStyles.cardSelectName}>{card.name}</span>
              </button>
            );
          })}
        </div>

        <div style={tradeModalStyles.actions}>
          <button style={tradeModalStyles.cancelButton} onClick={onCancel}>
            Cancel
          </button>
          <button
            style={{
              ...tradeModalStyles.confirmButton,
              opacity: selectedCards.size === 0 ? 0.5 : 1,
            }}
            onClick={handleConfirm}
            disabled={selectedCards.size === 0}
          >
            Request {selectedCards.size > 0 ? `(${selectedCards.size})` : ''}
          </button>
        </div>

      </div>

      {/* Toast notification - outside modal content so it's not clipped */}
      {toast && (
        <div style={tradeModalStyles.toast}>
          {toast}
        </div>
      )}
    </div>
  );
};

const tradeModalStyles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.85)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: 20,
  },
  content: {
    backgroundColor: 'var(--bg-card)',
    borderRadius: 16,
    border: '1px solid var(--border)',
    padding: 24,
    maxWidth: 320,
    width: '100%',
    textAlign: 'center',
  },
  multiContent: {
    backgroundColor: 'var(--bg-card)',
    borderRadius: 16,
    border: '1px solid var(--border)',
    padding: 24,
    maxWidth: 400,
    width: '100%',
    maxHeight: '80vh',
    overflow: 'auto',
  },
  title: {
    fontSize: 18,
    fontWeight: 700,
    color: 'var(--text)',
    margin: 0,
    marginBottom: 16,
    textAlign: 'center',
  },
  subtitle: {
    fontSize: 13,
    color: 'var(--text-secondary)',
    margin: 0,
    marginBottom: 16,
    textAlign: 'center',
  },
  cardImage: {
    width: 120,
    height: 168,
    objectFit: 'contain',
    borderRadius: 8,
    marginBottom: 16,
  },
  text: {
    fontSize: 14,
    color: 'var(--text-secondary)',
    margin: 0,
    marginBottom: 24,
    lineHeight: 1.5,
  },
  highlight: {
    color: 'var(--text)',
    fontWeight: 600,
  },
  actions: {
    display: 'flex',
    gap: 12,
    marginTop: 16,
  },
  cancelButton: {
    flex: 1,
    padding: '12px 16px',
    backgroundColor: 'var(--border)',
    border: 'none',
    borderRadius: 10,
    color: 'var(--text)',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
  },
  confirmButton: {
    flex: 1,
    padding: '12px 16px',
    backgroundColor: 'var(--accent)',
    border: 'none',
    borderRadius: 10,
    color: 'var(--bg-pure)',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
  },
  cardGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 10,
    marginBottom: 8,
  },
  cardSelectItem: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: 8,
    borderRadius: 10,
    border: '2px solid',
    cursor: 'pointer',
    background: 'none',
  },
  cardSelectImage: {
    width: '100%',
    height: 100,
    objectFit: 'contain',
    borderRadius: 4,
    marginBottom: 6,
  },
  cardSelectName: {
    fontSize: 10,
    color: 'var(--text)',
    textAlign: 'center',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    width: '100%',
  },
  checkBadge: {
    position: 'absolute',
    top: 6,
    right: 6,
    backgroundColor: 'var(--accent)',
    borderRadius: '50%',
    width: 20,
    height: 20,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'var(--bg-pure)',
  },
  requestedBadge: {
    position: 'absolute',
    top: 6,
    right: 6,
    backgroundColor: 'var(--warning)',
    borderRadius: 4,
    padding: '2px 6px',
    fontSize: 8,
    fontWeight: 700,
    color: 'var(--bg-pure)',
  },
  pcBadge: {
    position: 'absolute',
    top: 6,
    right: 6,
    backgroundColor: 'var(--text-secondary)',
    borderRadius: 4,
    padding: '2px 6px',
    fontSize: 8,
    fontWeight: 700,
    color: 'var(--text)',
  },
  toast: {
    position: 'fixed',
    bottom: 100,
    left: 20,
    right: 20,
    backgroundColor: 'var(--border)',
    borderRadius: 12,
    padding: '14px 20px',
    fontSize: 14,
    color: 'var(--text)',
    textAlign: 'center',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)',
    zIndex: 99999,
  },
};

const Home = () => {
  const { theme, isDark } = useTheme();
  const { user, session, signOut, loading } = useAuth();
  const { requireAuth } = useGuestPrompt();
  const navigate = useNavigate();
  const {
    showWelcomeTutorial,
    checkWelcomeTutorial,
    handleWelcomeTutorialYes,
    handleWelcomeTutorialNo,
    currentStep,
    setCurrentStep,
    handleProfileClicked,
    handleIntroChaseNext,
    handleChaseOptionSelected,
    handlePostNavClicked,
    handleStartTradeTutorial,
    handleLookForTrade,
    handleTradeButtonClicked,
    handleTradeBlockedContinue,
    handleTradeRequestSent,
    handleFirstTradeRequestNext,
    handleCongratsComplete,
    handleMoreTutorials,
    hideMyPosts,
    loading: tutorialLoading,
  } = useTutorial();
  const { isViewerOnly, activeProfile } = useProfile();
  const [profile, setProfile] = useState(null);
  const [showParentModal, setShowParentModal] = useState(false);
  const [profileLoading, setProfileLoading] = useState(true);
  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem('imagePreference') === 'photo' ? 'photo' : 'digital';
  });
  const [posts, setPosts] = useState([]);
  const [postsLoading, setPostsLoading] = useState(true);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [userRatings, setUserRatings] = useState({}); // { postId: 'poke' | 'great' | 'ultra' | 'master' }
  const [requestedCardIds, setRequestedCardIds] = useState(new Set()); // Cards user has already requested
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [reportingPost, setReportingPost] = useState(null);
  const [reportSuccessToast, setReportSuccessToast] = useState(false);
  const [tradeConfirmModal, setTradeConfirmModal] = useState(null); // { cardId, cardOwnerId, cardName, cardImage, ownerName }
  const [multiCardSelectModal, setMultiCardSelectModal] = useState(null); // { cards, ownerId, ownerName }
  const [filterOpen, setFilterOpen] = useState(false);
  const [locationFilter, setLocationFilter] = useState(null); // { mode, userLocation, radius, cityName, cityCoords }
  const [singlePostFilter, setSinglePostFilter] = useState(null); // postId from notification deep link
  const [searchParams, setSearchParams] = useSearchParams();
  const { searchOpen, openSearch, closeSearch, refreshHomeTrigger } = useUI();
  const restoredFromCacheRef = useRef(false);
  const lastRefreshTriggerRef = useRef(refreshHomeTrigger);
  const feedContainerRef = useRef(null);
  const [isScrollIdle, setIsScrollIdle] = useState(true);
  const scrollIdleTimerRef = useRef(null);
  const [hasMorePosts, setHasMorePosts] = useState(true);
  const [loadingMorePosts, setLoadingMorePosts] = useState(false);
  const [postsDbOffset, setPostsDbOffset] = useState(0); // Track actual DB offset
  const feedSentinelRef = useRef(null);
  const POSTS_PER_PAGE = 20;

  // Check for single post filter from notification deep link
  useEffect(() => {
    const postId = searchParams.get('postId');
    if (postId) {
      console.log('[Home] Single post filter from notification:', postId);
      setSinglePostFilter(postId);
      // Clear the query param but keep the filter active
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Check for welcome tutorial on first load
  useEffect(() => {
    if (!tutorialLoading && !postsLoading) {
      checkWelcomeTutorial();
    }
  }, [tutorialLoading, postsLoading, checkWelcomeTutorial]);

  // RETURNING user notification check - SILENT, no prompts
  // This fixes issues like iOS permission granted but OneSignal not opted in
  useEffect(() => {
    if (!Capacitor.isNativePlatform() || !user?.id) return;

    // Only check once per session
    const hasChecked = sessionStorage.getItem('notificationChecked');
    if (hasChecked) return;

    const checkNotifications = async () => {
      try {
        // Delay to let app stabilize
        await new Promise(resolve => setTimeout(resolve, 2000));

        const OneSignal = window.plugins?.OneSignal || window.OneSignal;
        if (!OneSignal) return;

        // Check if opted in
        const isOptedIn = OneSignal.User?.pushSubscription?.optedIn;
        console.log('[Home] Notification check - optedIn:', isOptedIn);

        if (!isOptedIn) {
          // Has subscription ID but not opted in? Fix it SILENTLY
          const subscriptionId = OneSignal.User?.pushSubscription?.id;
          if (subscriptionId) {
            console.log('[Home] Fixing notification opt-in status (silent)');
            OneSignal.User.pushSubscription.optIn();
          }
          // NOTE: If no subscriptionId, don't prompt - they can enable in Profile
        }

        // Re-register device to ensure current
        if (activeProfile?.id) {
          OneSignal.login(activeProfile.id);
          console.log('[Home] Re-registered device for profile:', activeProfile.id);
        }

        sessionStorage.setItem('notificationChecked', 'true');
      } catch (err) {
        console.error('[Home] Notification check error:', err);
      }
    };

    checkNotifications();
  }, [user?.id, activeProfile?.id]);

  // Cache posts and userRatings in sessionStorage when they load
  useEffect(() => {
    if (!postsLoading && posts.length > 0) {
      sessionStorage.setItem('cachedHomePosts', JSON.stringify(posts));
      sessionStorage.setItem('cachedUserRatings', JSON.stringify(userRatings));
    }
  }, [postsLoading, posts, userRatings]);

  // Restore cached posts, userRatings, and scroll position on mount
  // Only restore if navigating back (has scroll position), otherwise fetch fresh
  useEffect(() => {
    const savedScroll = sessionStorage.getItem('homeScrollPosition');
    const cachedPosts = sessionStorage.getItem('cachedHomePosts');
    const cachedRatings = sessionStorage.getItem('cachedUserRatings');

    // Only restore from cache if we have a saved scroll position (means user navigated away and back)
    // This ensures fresh data on initial load/refresh
    if (savedScroll && cachedPosts) {
      try {
        const parsedPosts = JSON.parse(cachedPosts);
        if (parsedPosts.length > 0) {
          restoredFromCacheRef.current = true;
          const scrollY = parseInt(savedScroll, 10);
          sessionStorage.removeItem('homeScrollPosition');
          setPosts(parsedPosts);
          // Restore user ratings too
          if (cachedRatings) {
            try {
              setUserRatings(JSON.parse(cachedRatings));
            } catch (e) {
              console.error('Error restoring cached ratings:', e);
            }
          }
          setPostsLoading(false);
          sessionStorage.setItem('pendingScrollRestore', scrollY.toString());
        }
      } catch (e) {
        console.error('Error restoring cached posts:', e);
      }
    } else {
      // No scroll position means fresh load - clear any stale cache
      sessionStorage.removeItem('cachedHomePosts');
      sessionStorage.removeItem('cachedUserRatings');
    }
  }, []);

  // Restore scroll after posts render
  useEffect(() => {
    const pendingScroll = sessionStorage.getItem('pendingScrollRestore');
    if (pendingScroll && posts.length > 0 && !postsLoading) {
      const scrollY = parseInt(pendingScroll, 10);
      sessionStorage.removeItem('pendingScrollRestore');

      const restoreScroll = () => {
        const container = feedContainerRef.current;
        if (container) container.scrollTop = scrollY;
      };

      restoreScroll();
      setTimeout(restoreScroll, 10);
      setTimeout(restoreScroll, 50);
      setTimeout(restoreScroll, 100);
      setTimeout(restoreScroll, 200);
      setTimeout(restoreScroll, 400);
    }
  }, [posts, postsLoading]);

  // Track scroll idle state
  useEffect(() => {
    const container = feedContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      // User is scrolling, set idle to false
      setIsScrollIdle(false);

      // Clear any existing timer
      if (scrollIdleTimerRef.current) {
        clearTimeout(scrollIdleTimerRef.current);
      }

      // Set idle to true after 500ms of no scroll
      scrollIdleTimerRef.current = setTimeout(() => {
        setIsScrollIdle(true);
      }, 500);
    };

    container.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      container.removeEventListener('scroll', handleScroll);
      if (scrollIdleTimerRef.current) {
        clearTimeout(scrollIdleTimerRef.current);
      }
    };
  }, []);

  // Save scroll position before navigating away
  const saveScrollAndNavigate = (path) => {
    const container = feedContainerRef.current;
    const scrollPos = container ? container.scrollTop : 0;
    sessionStorage.setItem('homeScrollPosition', scrollPos.toString());
    navigate(path);
  };

  // Sync image preference from localStorage (from Profile settings)
  useEffect(() => {
    const syncPreference = () => {
      const pref = localStorage.getItem('imagePreference');
      setViewMode(pref === 'photo' ? 'photo' : 'digital');
    };
    // Sync on focus (when returning from Profile)
    window.addEventListener('focus', syncPreference);
    // Sync on storage changes (cross-tab)
    window.addEventListener('storage', syncPreference);
    return () => {
      window.removeEventListener('focus', syncPreference);
      window.removeEventListener('storage', syncPreference);
    };
  }, []);

  // Set currentUserId based on activeProfile from ProfileContext
  // This uses the kid's ID when parent views as kid
  useEffect(() => {
    if (activeProfile?.id) {
      setProfile(activeProfile);
      setCurrentUserId(activeProfile.id);
      setProfileLoading(false);
    } else if (!user?.id) {
      setProfileLoading(false);
    }
  }, [activeProfile?.id, user?.id]);

  // Haversine formula to calculate distance between two points
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    if (!lat1 || !lon1 || !lat2 || !lon2) return null;
    const R = 3959; // Earth's radius in miles
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return Math.round(R * c);
  };

  // Fetch posts (with optional offset for pagination)
  const fetchPosts = useCallback(async (dbOffset = 0, append = false) => {
    try {
      if (append) {
        setLoadingMorePosts(true);
      } else {
        setPostsLoading(true);
        setHasMorePosts(true);
        setPostsDbOffset(0);
      }

      // Fetch more posts than needed to account for filtering
      const fetchLimit = POSTS_PER_PAGE * 3;

      // Fetch posts with user info (including location for filtering)
      const { data: postsData, error: postsError } = await supabase
        .from('posts')
        .select(`
          id,
          user_id,
          template_id,
          content,
          created_at,
          users!posts_user_id_fkey(id, auth_id, username, avatar_url, trade_level, latitude, longitude, location_city, location_state)
        `)
        .order('created_at', { ascending: false })
        .range(dbOffset, dbOffset + fetchLimit - 1);

      if (postsError) {
        console.error('Error fetching posts:', postsError);
        return;
      }

      if (!postsData || postsData.length === 0) {
        setHasMorePosts(false);
        if (!append) {
          setPosts([]);
        }
        return;
      }

      // Update the database offset for next fetch
      setPostsDbOffset(dbOffset + postsData.length);

      // Fetch cards for these posts
      const postIds = postsData.map(p => p.id);
      const { data: cardsData, error: cardsError } = await supabase
        .from('posts_cards')
        .select(`
          post_id,
          card_id,
          cards!posts_cards_card_id_fkey(
            id,
            name,
            grade,
            digital_image_url,
            original_image_url,
            back_photo,
            front_top_photo,
            front_bottom_photo,
            back_top_photo,
            back_bottom_photo,
            set_name,
            traded,
            trading_status,
            binder_id,
            hidden,
            binders(id, name)
          )
        `)
        .in('post_id', postIds);

      if (cardsError) {
        console.error('Error fetching cards:', cardsError);
      }

      // Group cards by post (filter out hidden and deleted cards)
      const cardsByPost = {};
      cardsData?.forEach(item => {
        if (item.cards && !item.cards.hidden && !item.cards.deleted_at) {
          if (!cardsByPost[item.post_id]) {
            cardsByPost[item.post_id] = [];
          }
          cardsByPost[item.post_id].push(item.cards);
        }
      });

      // Fetch reactions (pokeball ratings) for these posts
      const { data: reactionsData, error: reactionsError } = await supabase
        .from('reactions')
        .select('post_id, reaction_type, user_id')
        .in('post_id', postIds);

      if (reactionsError && reactionsError.code !== 'PGRST116') {
        console.error('Error fetching reactions:', reactionsError);
      }

      // Group reactions by post and count by type
      const ratingsByPost = {};
      const userRatingsByPost = {};
      reactionsData?.forEach(reaction => {
        // Initialize post ratings if needed
        if (!ratingsByPost[reaction.post_id]) {
          ratingsByPost[reaction.post_id] = { poke: 0, great: 0, ultra: 0, master: 0 };
        }
        // Count the reaction
        if (reaction.reaction_type && ratingsByPost[reaction.post_id][reaction.reaction_type] !== undefined) {
          ratingsByPost[reaction.post_id][reaction.reaction_type]++;
        }
        // Track current user's reaction
        if (currentUserId && reaction.user_id === currentUserId) {
          userRatingsByPost[reaction.post_id] = reaction.reaction_type;
        }
      });

      // Set user's existing ratings
      setUserRatings(userRatingsByPost);

      // Check follow status for each user
      let followingIds = [];
      if (currentUserId) {
        const { data: followData } = await supabase
          .from('user_follows')
          .select('following_id')
          .eq('follower_id', currentUserId);
        followingIds = followData?.map(f => f.following_id) || [];

        // Fetch user's existing trade requests (pending or accepted)
        const { data: tradeRequestsData } = await supabase
          .from('trade_requests')
          .select('card_id')
          .eq('requester_id', currentUserId)
          .in('status', ['pending', 'accepted']);

        const requestedIds = new Set(tradeRequestsData?.map(r => r.card_id) || []);
        setRequestedCardIds(requestedIds);
      }

      // Format time ago
      const formatTimeAgo = (dateStr) => {
        const date = new Date(dateStr);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return 'just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        if (diffDays < 7) return `${diffDays}d ago`;
        return date.toLocaleDateString();
      };

      // Get filter coordinates
      let filterLat = null;
      let filterLon = null;
      let filterRadius = 50;

      if (locationFilter) {
        filterRadius = locationFilter.radius || 50;
        if (locationFilter.mode === 'nearby' && locationFilter.userLocation) {
          filterLat = locationFilter.userLocation.latitude;
          filterLon = locationFilter.userLocation.longitude;
        } else if (locationFilter.mode === 'city' && locationFilter.cityCoords) {
          filterLat = locationFilter.cityCoords.latitude;
          filterLon = locationFilter.cityCoords.longitude;
        }
      }

      // Get current user's location for distance display (always show distance if user has location)
      const myLat = profile?.latitude;
      const myLon = profile?.longitude;

      // Transform posts
      const transformedPosts = postsData
        .filter(post => cardsByPost[post.id]?.length > 0)
        .map(post => {
          const cards = cardsByPost[post.id] || [];
          const firstCard = cards[0];
          const userLat = post.users?.latitude;
          const userLon = post.users?.longitude;

          // Calculate distance from current user (for display - always calculate if both have location)
          let distance = null;
          if (myLat && myLon && userLat && userLon) {
            distance = calculateDistance(myLat, myLon, userLat, userLon);
          }

          // For filter, use filter coordinates if active
          let filterDistance = null;
          if (filterLat && filterLon && userLat && userLon) {
            filterDistance = calculateDistance(filterLat, filterLon, userLat, userLon);
          }

          return {
            id: post.id,
            postType: post.template_id || 'Post',
            timestamp: formatTimeAgo(post.created_at),
            user: {
              id: post.users?.id,
              auth_id: post.users?.auth_id,
              username: post.users?.username || 'Unknown',
              avatar: post.users?.avatar_url,
              tradeLevel: post.users?.trade_level || 1,
              followed: followingIds.includes(post.users?.id),
              city: post.users?.location_city,
              state: post.users?.location_state,
              latitude: userLat,
              longitude: userLon,
            },
            distance,
            filterDistance,
            card: {
              id: firstCard?.id,
              name: firstCard?.name || 'Unknown Card',
              grade: firstCard?.grade || 'N/A',
              digitalImage: firstCard?.digital_image_url,
              originalImage: firstCard?.original_image_url,
              backPhoto: firstCard?.back_photo,
              frontTopPhoto: firstCard?.front_top_photo,
              frontBottomPhoto: firstCard?.front_bottom_photo,
              backTopPhoto: firstCard?.back_top_photo,
              backBottomPhoto: firstCard?.back_bottom_photo,
              set: firstCard?.set_name,
              traded: firstCard?.traded,
              trading_status: firstCard?.trading_status || 'PC',
              binderId: firstCard?.binder_id || null,
              binderName: firstCard?.binders?.name || null,
            },
            type: cards.length > 1 ? 'multi' : 'single',
            cards: cards.map(c => ({
              id: c.id,
              name: c.name,
              grade: c.grade,
              digitalImage: c.digital_image_url,
              originalImage: c.original_image_url,
              backPhoto: c.back_photo,
              frontTopPhoto: c.front_top_photo,
              frontBottomPhoto: c.front_bottom_photo,
              backTopPhoto: c.back_top_photo,
              backBottomPhoto: c.back_bottom_photo,
              trading_status: c.trading_status,
              binderName: c.binders?.name || null,
              hidden: c.hidden || false,
            })),
            ratings: ratingsByPost[post.id] || { poke: 0, great: 0, ultra: 0, master: 0 },
          };
        })
        // Filter by following if following mode is active
        .filter(post => {
          if (locationFilter?.mode === 'following') {
            return post.user.followed;
          }
          return true;
        })
        // Filter by location if location filter is active
        .filter(post => {
          if (!locationFilter || locationFilter.mode === 'following') return true;
          if (!filterLat || !filterLon) return true;
          // Only show posts from users with location set and within radius
          if (post.filterDistance === null) return false;
          return post.filterDistance <= filterRadius;
        })
        // Sort by distance if filtering by location (use filterDistance for sort order)
        .sort((a, b) => {
          if (locationFilter && locationFilter.mode !== 'following' && a.filterDistance !== null && b.filterDistance !== null) {
            return a.filterDistance - b.filterDistance;
          }
          return 0;
        });

      // Check if we have more posts to load
      if (postsData.length < fetchLimit) {
        setHasMorePosts(false);
      }

      if (append) {
        setPosts(prev => [...prev, ...transformedPosts]);
      } else {
        setPosts(transformedPosts);
      }
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setPostsLoading(false);
      setLoadingMorePosts(false);
    }
  }, [currentUserId, locationFilter, profile]);

  useEffect(() => {
    // Fetch for authed users OR guests (Apple 5.1.1(v) - public browsing)
    if (currentUserId !== null || isGuest()) {
      // Check if refresh was triggered (new post created)
      const refreshTriggered = refreshHomeTrigger !== lastRefreshTriggerRef.current;
      lastRefreshTriggerRef.current = refreshHomeTrigger;

      // Skip fetch if we just restored from cache, unless refresh was triggered
      if (restoredFromCacheRef.current && !refreshTriggered) {
        restoredFromCacheRef.current = false; // Reset for next time
        return;
      }

      fetchPosts();
    }
  }, [currentUserId, fetchPosts, refreshHomeTrigger]);

  // Load more posts when scrolling to bottom
  const loadMorePosts = useCallback(() => {
    if (loadingMorePosts || !hasMorePosts) return;
    fetchPosts(postsDbOffset, true);
  }, [fetchPosts, loadingMorePosts, hasMorePosts, postsDbOffset]);

  // Infinite scroll observer
  useEffect(() => {
    const sentinel = feedSentinelRef.current;
    if (!sentinel || !hasMorePosts || loadingMorePosts || postsLoading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMorePosts();
        }
      },
      { threshold: 0, rootMargin: '200px' } // Trigger 200px before reaching bottom
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMorePosts, loadingMorePosts, postsLoading, loadMorePosts]);

  // Toggle follow
  const handleToggleFollow = async (userId) => {
    if (requireAuth('follow this trader')) return;
    if (!currentUserId) return;

    // Optimistic update
    setPosts(prev => prev.map(post => {
      if (post.user.id === userId) {
        return {
          ...post,
          user: { ...post.user, followed: !post.user.followed }
        };
      }
      return post;
    }));

    try {
      const isFollowing = posts.find(p => p.user.id === userId)?.user.followed;

      if (isFollowing) {
        // Unfollow
        await supabase
          .from('user_follows')
          .delete()
          .eq('follower_id', currentUserId)
          .eq('following_id', userId);
      } else {
        // Follow
        await supabase
          .from('user_follows')
          .insert({
            follower_id: currentUserId,
            following_id: userId,
          });

        // Send notification to the user being followed
        const followerName = profile?.username || 'Someone';
        sendNewFollowerNotification(userId, followerName, currentUserId);
      }
    } catch (err) {
      console.error('Error toggling follow:', err);
      // Revert on error
      setPosts(prev => prev.map(post => {
        if (post.user.id === userId) {
          return {
            ...post,
            user: { ...post.user, followed: !post.user.followed }
          };
        }
        return post;
      }));
    }
  };

  // Handle rating selection
  const handleRatingSelect = async (postId, ratingType) => {
    const previousRating = userRatings[postId];

    // Optimistic update - toggle off if same rating, otherwise set new rating
    if (previousRating === ratingType) {
      // Toggle off
      setUserRatings(prev => {
        const updated = { ...prev };
        delete updated[postId];
        return updated;
      });

      // Update post ratings count
      setPosts(prev => prev.map(post => {
        if (post.id === postId) {
          return {
            ...post,
            ratings: {
              ...post.ratings,
              [ratingType]: Math.max(0, (post.ratings?.[ratingType] || 1) - 1),
            },
          };
        }
        return post;
      }));
    } else {
      // Set new rating
      setUserRatings(prev => ({ ...prev, [postId]: ratingType }));

      // Update post ratings count
      setPosts(prev => prev.map(post => {
        if (post.id === postId) {
          const newRatings = { ...post.ratings };
          // Decrease previous if exists
          if (previousRating) {
            newRatings[previousRating] = Math.max(0, (newRatings[previousRating] || 1) - 1);
          }
          // Increase new
          newRatings[ratingType] = (newRatings[ratingType] || 0) + 1;
          return { ...post, ratings: newRatings };
        }
        return post;
      }));
    }

    // Save to database (reactions table)
    try {
      if (previousRating === ratingType) {
        // Remove reaction
        await supabase
          .from('reactions')
          .delete()
          .eq('post_id', postId)
          .eq('user_id', currentUserId);
      } else if (previousRating) {
        // Update existing reaction
        await supabase
          .from('reactions')
          .update({ reaction_type: ratingType })
          .eq('post_id', postId)
          .eq('user_id', currentUserId);
      } else {
        // Insert new reaction
        await supabase
          .from('reactions')
          .insert({
            post_id: postId,
            user_id: currentUserId,
            reaction_type: ratingType,
          });

        // Send notification to post owner (only for new reactions, not updates)
        const post = posts.find(p => p.id === postId);
        if (post && post.user?.id && post.user.id !== currentUserId) {
          const reactorName = profile?.username || 'Someone';
          const cardName = post.card?.name || post.cards?.[0]?.name || 'your card';
          sendReactionNotification(post.user.id, reactorName, cardName, post.card?.id || post.cards?.[0]?.id, ratingType);
        }
      }
    } catch (err) {
      console.error('Error saving reaction:', err);
    }
  };

  // Handle report
  const handleReport = (post) => {
    if (requireAuth('report a post')) return;
    setReportingPost(post);
    setReportModalOpen(true);
  };

  // Handle successful report
  const handleReportSuccess = (post) => {
    // Mark the reported card as hidden in the local state
    setPosts(prev => prev.map(p => {
      if (p.id !== post.id) return p;

      // Mark the card as hidden
      const updatedCards = p.cards.map(c =>
        c.id === post.card?.id ? { ...c, hidden: true } : c
      );

      // Filter out hidden cards
      const visibleCards = updatedCards.filter(c => !c.hidden);

      // If no visible cards left, remove the post entirely
      if (visibleCards.length === 0) return null;

      // Update the post with visible cards only
      return {
        ...p,
        cards: visibleCards,
        card: visibleCards[0] || p.card,
        type: visibleCards.length > 1 ? 'multi' : 'single',
      };
    }).filter(Boolean)); // Remove null entries (posts with no visible cards)

    // Show success toast
    setReportSuccessToast(true);
    setTimeout(() => setReportSuccessToast(false), 3000);
  };

  // Handle delete own post
  const handleDeletePost = async (post) => {
    if (!window.confirm('Delete this post? This cannot be undone.')) return;

    try {
      // Remove all posts_cards entries for this post
      await supabase
        .from('posts_cards')
        .delete()
        .eq('post_id', post.id);

      // Delete the post itself
      await supabase
        .from('posts')
        .delete()
        .eq('id', post.id);

      // Remove from local state
      setPosts(prev => prev.filter(p => p.id !== post.id));

      // Clear cache
      sessionStorage.removeItem('cachedHomePosts');
    } catch (err) {
      console.error('Error deleting post:', err);
      alert('Failed to delete post. Please try again.');
    }
  };

  // Execute the actual trade request API call
  const executeSendTradeRequest = async (cardId, cardOwnerId, cardName, skipNotification = false) => {
    if (!currentUserId || !cardId || !cardOwnerId) return;

    // Optimistic update
    setRequestedCardIds(prev => new Set([...prev, cardId]));

    try {
      // Check for existing request first
      const { data: existingRequest } = await supabase
        .from('trade_requests')
        .select('id')
        .eq('requester_id', currentUserId)
        .eq('card_id', cardId)
        .in('status', ['pending', 'accepted'])
        .single();

      if (existingRequest) {
        return; // Already requested
      }

      // Insert new trade request
      const { error } = await supabase
        .from('trade_requests')
        .insert({
          requester_id: currentUserId,
          card_owner_id: cardOwnerId,
          card_id: cardId,
          status: 'pending',
          viewed_by_owner: false,
        });

      if (error) {
        console.error('Error sending trade request:', error);
        // Revert optimistic update
        setRequestedCardIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(cardId);
          return newSet;
        });
      } else {
        // Success - trigger first trade request tip if applicable
        handleTradeRequestSent();

        // Send push notification to card owner (unless skipped for batch operations)
        if (!skipNotification) {
          const requesterName = profile?.username || 'Someone';
          sendTradeRequestNotification(cardOwnerId, requesterName, cardName || 'a card');
        }
      }
    } catch (err) {
      console.error('Error sending trade request:', err);
      // Revert optimistic update
      setRequestedCardIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(cardId);
        return newSet;
      });
    }
  };

  // Handle trade button click - shows confirmation modal
  const handleSendTradeRequest = (cardId, cardOwnerId, cardName, cardImage, ownerName) => {
    if (requireAuth('send a trade request')) return;
    // Viewer-only restriction: Block trading for minors without parent permission
    if (isViewerOnly) {
      setShowParentModal(true);
      return;
    }

    // Check if we're in tutorial mode - block the request
    if (handleTradeButtonClicked()) {
      return; // Tutorial blocked it
    }

    // Show confirmation modal
    setTradeConfirmModal({
      cardId,
      cardOwnerId,
      cardName,
      cardImage,
      ownerName: ownerName || 'this user',
    });
  };

  // Handle trade button click for multi-card posts
  const handleMultiCardTradeClick = (post) => {
    if (requireAuth('send a trade request')) return;
    // Viewer-only restriction: Block trading for minors without parent permission
    if (isViewerOnly) {
      setShowParentModal(true);
      return;
    }

    // Check if we're in tutorial mode - block the request
    if (handleTradeButtonClicked()) {
      return; // Tutorial blocked it
    }

    // Show multi-card selection modal
    setMultiCardSelectModal({
      cards: post.cards || [],
      ownerId: post.user.id,
      ownerName: post.user.username || 'this user',
    });
  };

  // Confirm single trade request
  const handleConfirmTrade = () => {
    if (tradeConfirmModal) {
      executeSendTradeRequest(
        tradeConfirmModal.cardId,
        tradeConfirmModal.cardOwnerId,
        tradeConfirmModal.cardName
      );
      setTradeConfirmModal(null);
    }
  };

  // Confirm multi-card trade requests
  const handleConfirmMultiTrade = async (selectedCardIds) => {
    if (multiCardSelectModal) {
      const { cards, ownerId } = multiCardSelectModal;
      const cardNames = [];

      // Send request for each selected card (without individual notifications)
      for (const cardId of selectedCardIds) {
        const card = cards.find(c => c.id === cardId);
        if (card) {
          await executeSendTradeRequest(cardId, ownerId, card.name, true); // skipNotification = true
          cardNames.push(card.name);
        }
      }

      // Send one combined notification for all cards
      if (cardNames.length > 0) {
        try {
          const requesterName = profile?.username || 'Someone';
          sendMultiTradeRequestNotification(ownerId, requesterName, cardNames);
        } catch (notifErr) {
          console.log('Multi-trade notification failed:', notifErr);
        }
      }

      setMultiCardSelectModal(null);
    }
  };

  // Handle block user
  const handleBlockUser = async (userId) => {
    if (requireAuth('block a user')) return;
    if (!currentUserId || !userId) return;

    try {
      // Insert into user_blocks table
      const { error } = await supabase
        .from('user_blocks')
        .insert({
          blocker_id: currentUserId,
          blocked_id: userId,
        });

      if (error) {
        if (error.code === '23505') {
          // Already blocked
          alert('User is already blocked');
        } else {
          throw error;
        }
      } else {
        // Remove blocked user's posts from feed
        setPosts(prev => prev.filter(post => post.user.id !== userId));
        alert('User blocked. You will no longer see their posts.');
      }
    } catch (err) {
      console.error('Error blocking user:', err);
      alert('Failed to block user');
    }
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  const handleResetIntake = async () => {
    if (!user) return;
    try {
      const { error } = await supabase
        .from('users')
        .update({ intake_completed: false })
        .eq('auth_id', user.id);

      if (error) throw error;
      navigate('/title');
    } catch (err) {
      console.error('Error resetting intake:', err);
      alert('Error resetting intake');
    }
  };

  // Format date from YYYY-MM-DD to MM/DD/YYYY
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      return `${parts[1]}/${parts[2]}/${parts[0]}`;
    }
    return dateStr;
  };

  return (
    <div style={styles(theme).container}>
      {/* Persistent backdrop that stays mounted during all tutorial steps */}
      <TutorialBackdrop
        visible={
          showWelcomeTutorial ||
          currentStep === TUTORIAL_STEPS.YES_INTRO_CHASE ||
          currentStep === TUTORIAL_STEPS.YES_CHASE_HELLO ||
          currentStep === TUTORIAL_STEPS.ADD_CARD_BINDER_NAV ||
          currentStep === TUTORIAL_STEPS.POST_CARD_NAV ||
          currentStep === TUTORIAL_STEPS.TRADE_FIND_CARD ||
          currentStep === TUTORIAL_STEPS.TRADE_BLOCKED ||
          currentStep === TUTORIAL_STEPS.FIRST_POST_CONGRATS ||
          currentStep === TUTORIAL_STEPS.FIRST_TRADE_REQUEST_TIP
        }
      />

      {/* Welcome Tutorial Overlay - Initial question */}
      <TutorialOverlay
        visible={showWelcomeTutorial}
        message={[
          "Alright!",
          "",
          "Now that we are inside the App,",
          "Would you like a tutorial or would",
          "you like to figure it out on your",
          "own?"
        ]}
        onYes={handleWelcomeTutorialYes}
        onNo={handleWelcomeTutorialNo}
      />

      {/* Point to Profile overlay - after "No thanks" (uses spotlight, has its own backdrop) */}
      <TutorialOverlay
        visible={currentStep === TUTORIAL_STEPS.POINT_TO_PROFILE}
        message={[
          "No problem!",
          "",
          "But before I let you go,",
          "click on your Profile so I can",
          "show you something important!"
        ]}
        hideButtons={true}
        spotlightMode="profile"
      />

      {/* YES flow - Chef introduces Chase */}
      <TutorialOverlay
        visible={currentStep === TUTORIAL_STEPS.YES_INTRO_CHASE}
        mode="intro-chase"
        message={[
          "Let me introduce you to my",
          "partner Chase!",
          "",
          "He is going to take over from",
          "here."
        ]}
        onNext={handleIntroChaseNext}
      />

      {/* YES flow - Chase says hello */}
      <TutorialOverlay
        visible={currentStep === TUTORIAL_STEPS.YES_CHASE_HELLO}
        mode="chase-hello"
        message={[
          "Hello! I'm Chase!",
          "",
          "What can I help you do?"
        ]}
        onOption1={() => handleChaseOptionSelected('add_card_binder')}
        onOption2={isViewerOnly ? undefined : () => handleChaseOptionSelected('post_card')}
        onOption3={isViewerOnly ? undefined : () => handleChaseOptionSelected('set_up_trade')}
        onClose={() => setCurrentStep(null)}
      />

      {/* Add Card/Binder flow - Step 1: Point to Cards nav (uses spotlight, has its own backdrop) */}
      <TutorialOverlay
        visible={currentStep === TUTORIAL_STEPS.ADD_CARD_BINDER_NAV}
        spotlightMode="bottom-nav-cards"
        message={[
          "First, click on Cards here",
          "at the bottom!",
          "",
          "This is where your cards, binders,",
          "and collection are!"
        ]}
      />

      {/* Post Card flow - Spotlight the Post button in center of nav */}
      <TutorialOverlay
        visible={currentStep === TUTORIAL_STEPS.POST_CARD_NAV}
        spotlightMode="bottom-nav-add"
        message={[
          "Click Post here to share",
          "a card with the community!",
          "",
          "You can post cards you want",
          "to trade or sell."
        ]}
        onYes={handlePostNavClicked}
      />

      {/* Trade Tutorial - Step 1: Find a Trade button */}
      <TutorialOverlay
        visible={currentStep === TUTORIAL_STEPS.TRADE_FIND_CARD}
        mode="normal"
        characterImage={ChaseWaveImage}
        message={[
          "Let's find a card to trade!",
          "",
          "Look for the red *Trade* button",
          "on someone else's post."
        ]}
        hideButtons={false}
        yesText="Look for Trade"
        noText=""
        onYes={handleLookForTrade}
      />

      {/* Trade Tutorial - Step 3: Trade blocked message */}
      <TutorialOverlay
        visible={currentStep === TUTORIAL_STEPS.TRADE_BLOCKED}
        mode="normal"
        characterImage={ChaseThumbsUpImage}
        message={[
          "You found it!",
          "",
          "Don't worry, it didn't work.",
          "You think I would let you send",
          "a request during a tutorial?"
        ]}
        hideButtons={false}
        yesText="Show me what's next"
        noText=""
        onYes={handleTradeBlockedContinue}
      />

      {/* Trade Tutorial - Arrows pointing to Trade buttons */}
      <TradeButtonArrows visible={currentStep === TUTORIAL_STEPS.TRADE_LOOKING} />

      {/* Trade Tutorial - Step 4: Point to Chat nav */}
      <TutorialOverlay
        visible={currentStep === TUTORIAL_STEPS.TRADE_CHAT_NAV}
        spotlightMode="bottom-nav-chat"
        message={[
          "Click Chat to see your",
          "trade requests dashboard!",
          "",
          "This is where all your",
          "trades live."
        ]}
      />

      {/* First Post Congrats */}
      <TutorialOverlay
        visible={currentStep === TUTORIAL_STEPS.FIRST_POST_CONGRATS}
        mode="chase-congrats"
        message={[
          "Awesome! You just created",
          "your first post!",
          "",
          "Keep sharing your",
          "collection!"
        ]}
        onContinue={handleCongratsComplete}
        continueText="Got it!"
        onSecondary={() => {
          handleMoreTutorials();
        }}
        secondaryText="More Tutorials"
        onClose={handleCongratsComplete}
      />

      {/* First Trade Request Tip - shows after sending first trade request from Home */}
      <TutorialOverlay
        visible={currentStep === TUTORIAL_STEPS.FIRST_TRADE_REQUEST_TIP}
        mode="normal"
        characterImage={ChaseWaveImage}
        message={[
          "There you go!",
          "",
          "Let's see if they're interested",
          "in your collection!",
          "Here's a tip..."
        ]}
        hideButtons={false}
        yesText="Next"
        noText=""
        onYes={handleFirstTradeRequestNext}
      />

      {/* Header */}
      <div style={styles(theme).header}>
        <h1 style={styles(theme).headerTitle}>Posts</h1>
        <div style={styles(theme).headerButtons}>
          <button style={styles(theme).iconButton} onClick={openSearch}>
            <SearchIcon />
          </button>
          <button
            style={{
              ...styles(theme).iconButton,
              ...(locationFilter ? styles(theme).filterButtonActive : {}),
            }}
            onClick={() => setFilterOpen(true)}
          >
            <FilterIcon />
            {locationFilter && (
              <div style={styles(theme).filterBadge}>
                <MapPinIcon />
              </div>
            )}
          </button>
          <button
            data-tutorial="profile-btn"
            style={styles(theme).iconButton}
            onClick={() => {
              handleProfileClicked();
              navigate('/profile');
            }}
          >
            <UserIcon />
          </button>
        </div>
        <div style={styles(theme).headerBorder} />
      </div>

      {/* Push Notification Prompt - shows if not enabled */}
      <PushNotificationPrompt />

      {/* Active Filter Indicator */}
      {locationFilter && (
        <div style={styles(theme).filterIndicator}>
          {locationFilter.mode === 'following' ? <UsersIcon /> : <MapPinIcon />}
          <span style={styles(theme).filterIndicatorText}>
            {locationFilter.mode === 'following'
              ? 'Showing traders I follow'
              : locationFilter.mode === 'nearby'
              ? `Within ${locationFilter.radius} mi of ${locationFilter.locationName || 'you'}`
              : `Within ${locationFilter.radius} mi of ${locationFilter.cityName}`}
          </span>
          <button
            style={styles(theme).filterIndicatorClear}
            onClick={() => {
              setLocationFilter(null);
              sessionStorage.removeItem('cachedHomePosts');
              sessionStorage.removeItem('cachedUserRatings');
            }}
          >
            Clear
          </button>
        </div>
      )}

      {/* Single Post Filter Indicator - from notification deep link */}
      {singlePostFilter && (
        <div style={styles(theme).filterIndicator}>
          <span style={{ fontSize: 16 }}>*</span>
          <span style={styles(theme).filterIndicatorText}>
            Viewing single post from notification
          </span>
          <button
            style={styles(theme).filterIndicatorClear}
            onClick={() => setSinglePostFilter(null)}
          >
            Show All
          </button>
        </div>
      )}

      {/* Main Content - Feed */}
      <div ref={feedContainerRef} data-tutorial="home-feed" style={styles(theme).feedContainer}>
        {postsLoading ? (
          // Show skeletons while loading
          <>
            <SkeletonPost />
            <SkeletonPost />
            <SkeletonPost />
          </>
        ) : posts.length > 0 ? (
          // Show posts - filter by single post if from notification, then filter own posts if hideMyPosts
          posts
            .filter(post => !singlePostFilter || post.id === singlePostFilter)
            .filter(post => !hideMyPosts || post.user.id !== currentUserId)
            .map(post => (
            post.type === 'multi' ? (
              <MultiCardPost
                key={post.id}
                post={post}
                viewMode={viewMode}
                currentUserId={currentUserId}
                onToggleFollow={handleToggleFollow}
                onReport={handleReport}
                onBlock={handleBlockUser}
                onDeletePost={handleDeletePost}
                userRatings={userRatings}
                onRatingSelect={handleRatingSelect}
                onNavigate={saveScrollAndNavigate}
                isScrollIdle={isScrollIdle}
                requestedCardIds={requestedCardIds}
                onSendTradeRequest={handleSendTradeRequest}
                onMultiCardTradeClick={handleMultiCardTradeClick}
              />
            ) : (
              <SingleCardPost
                key={post.id}
                post={post}
                viewMode={viewMode}
                currentUserId={currentUserId}
                onToggleFollow={handleToggleFollow}
                onReport={handleReport}
                onBlock={handleBlockUser}
                onDeletePost={handleDeletePost}
                userRatings={userRatings}
                onRatingSelect={handleRatingSelect}
                onNavigate={saveScrollAndNavigate}
                isScrollIdle={isScrollIdle}
                requestedCardIds={requestedCardIds}
                onSendTradeRequest={handleSendTradeRequest}
              />
            )
          ))
        ) : (
          // Empty state
          <div style={styles(theme).emptyState}>
            <p style={styles(theme).emptyStateText}>No posts yet</p>
            <p style={styles(theme).emptyStateSubtext}>Be the first to share a card!</p>
          </div>
        )}

        {/* Infinite scroll sentinel */}
        {posts.length > 0 && hasMorePosts && (
          <div ref={feedSentinelRef} style={{ height: '20px' }} />
        )}

        {/* Loading more indicator */}
        {loadingMorePosts && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
            <div style={{
              width: '24px',
              height: '24px',
              border: `2px solid ${theme.border}`,
              borderTopColor: theme.accent,
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
            }} />
          </div>
        )}
      </div>


      {/* Search Modal */}
      <Search
        isOpen={searchOpen}
        onClose={closeSearch}
        currentUserId={currentUserId}
        viewMode={viewMode}
      />

      {/* Report Modal */}
      <ReportModal
        isOpen={reportModalOpen}
        onClose={() => {
          setReportModalOpen(false);
          setReportingPost(null);
        }}
        post={reportingPost}
        currentUserId={currentUserId}
        onSuccess={handleReportSuccess}
      />

      {/* Report Success Toast */}
      <AnimatePresence>
        {reportSuccessToast && (
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            style={{
              position: 'fixed',
              bottom: 100,
              left: '50%',
              transform: 'translateX(-50%)',
              backgroundColor: theme.bgCard,
              borderRadius: 12,
              padding: '12px 20px',
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.25)',
              zIndex: 9999,
              border: `1px solid ${theme.border}`,
            }}
          >
            <div style={{
              width: 28,
              height: 28,
              borderRadius: 14,
              backgroundColor: 'rgba(34, 197, 94, 0.15)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.5">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <span style={{ color: theme.text, fontSize: 14, fontWeight: 500 }}>
              Report submitted - thank you!
            </span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Location Filter */}
      <LocationFilter
        isOpen={filterOpen}
        onClose={() => setFilterOpen(false)}
        onApplyFilter={(filter) => {
          setLocationFilter(filter);
          // Clear cache so filtered results are fresh
          sessionStorage.removeItem('cachedHomePosts');
          sessionStorage.removeItem('cachedUserRatings');
        }}
        currentFilter={locationFilter}
        userProfile={profile}
      />

      {/* Trade Confirmation Modal - Single Card */}
      <TradeConfirmModal
        data={tradeConfirmModal}
        onConfirm={handleConfirmTrade}
        onCancel={() => setTradeConfirmModal(null)}
      />

      {/* Multi-Card Selection Modal */}
      <MultiCardSelectModal
        data={multiCardSelectModal}
        onConfirm={handleConfirmMultiTrade}
        onCancel={() => setMultiCardSelectModal(null)}
        requestedCardIds={requestedCardIds}
      />

      {/* Add Parent Modal for viewer-only users */}
      <AddParentModal
        isOpen={showParentModal}
        onClose={() => setShowParentModal(false)}
        feature="trading"
      />

    </div>
  );
};

const styles = (theme) => ({
  container: {
    height: '100%',
    backgroundColor: theme.bg,
    display: 'flex',
    flexDirection: 'column',
  },
  // Header styles
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
    backgroundColor: theme.bg,
    flexShrink: 0,
    flexWrap: 'wrap',
    position: 'relative',
  },
  headerBorder: {
    position: 'absolute',
    bottom: 0,
    left: '40px',
    right: '40px',
    height: '2px',
    backgroundColor: 'rgba(255, 255, 255, 0.35)',
    borderRadius: '1px',
  },
  headerTitle: {
    color: theme.text,
    fontSize: '20px',
    fontWeight: '700',
    margin: 0,
  },
  headerButtons: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  iconButton: {
    width: '36px',
    height: '36px',
    borderRadius: '10px',
    backgroundColor: theme.bgCard,
    border: 'none',
    boxShadow: `inset 0 0 0 1px ${theme.border}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: theme.textSecondary,
    transition: 'all 0.15s ease',
    position: 'relative',
    outline: 'none',
    WebkitTapHighlightColor: 'transparent',
    WebkitAppearance: 'none',
  },
  filterButtonActive: {
    backgroundColor: 'rgba(14, 165, 233, 0.15)',
    boxShadow: `inset 0 0 0 1px ${theme.accent}`,
    color: theme.accent,
  },
  filterBadge: {
    position: 'absolute',
    top: '-4px',
    right: '-4px',
    width: '16px',
    height: '16px',
    borderRadius: '50%',
    backgroundColor: theme.accent,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.text,
  },
  filterIndicator: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 16px',
    backgroundColor: 'rgba(14, 165, 233, 0.1)',
    borderBottom: '1px solid rgba(14, 165, 233, 0.2)',
    color: theme.accent,
  },
  filterIndicatorText: {
    flex: 1,
    fontSize: '13px',
    fontWeight: '500',
  },
  filterIndicatorClear: {
    padding: '4px 10px',
    backgroundColor: 'transparent',
    border: '1px solid rgba(14, 165, 233, 0.4)',
    borderRadius: '6px',
    color: theme.accent,
    fontSize: '12px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  viewModeButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 12px',
    borderRadius: '10px',
    backgroundColor: theme.bgCard,
    border: `1px solid ${theme.border}`,
    cursor: 'pointer',
    color: theme.accent,
    transition: 'all 0.15s ease',
  },
  viewModeText: {
    fontSize: '13px',
    fontWeight: '500',
    color: theme.accent,
  },
  // Feed styles
  feedContainer: {
    flex: 1,
    overflow: 'auto',
    padding: '16px',
    paddingBottom: '100px', // Space for bottom nav
  },
  feedPlaceholder: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    gap: '8px',
  },
  placeholderText: {
    color: theme.textSecondary,
    fontSize: '16px',
    margin: 0,
  },
  placeholderSubtext: {
    color: theme.textSecondary,
    fontSize: '14px',
    margin: 0,
  },
  // Panel overlay styles
  panelOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    display: 'flex',
    justifyContent: 'flex-end',
    zIndex: 1000,
  },
  panelContainer: {
    width: '100%',
    maxWidth: '400px',
    height: '100%',
    backgroundColor: theme.bg,
    display: 'flex',
    flexDirection: 'column',
    animation: 'slideIn 0.2s ease',
  },
  panelHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px',
    borderBottom: `1px solid ${theme.bgCard}`,
    flexShrink: 0,
  },
  panelTitle: {
    color: theme.text,
    fontSize: '18px',
    fontWeight: '600',
    margin: 0,
  },
  panelCloseButton: {
    width: '36px',
    height: '36px',
    borderRadius: '10px',
    backgroundColor: theme.bgCard,
    border: `1px solid ${theme.border}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: theme.textSecondary,
  },
  panelContent: {
    flex: 1,
    overflow: 'auto',
    padding: '16px',
  },
  // Section styles (reused in panel)
  section: {
    marginBottom: '20px',
    padding: '16px',
    backgroundColor: theme.bgSheet,
    borderRadius: '8px',
    border: `1px solid ${theme.border}`,
  },
  sectionTitle: {
    color: theme.textMuted,
    fontSize: '12px',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    margin: '0 0 12px 0',
  },
  label: {
    color: theme.textSecondary,
    fontSize: '14px',
  },
  value: {
    color: theme.text,
    fontSize: '14px',
  },
  valueHighlight: {
    color: theme.accent,
    fontSize: '14px',
    fontWeight: '600',
  },
  valueSmall: {
    color: theme.text,
    fontSize: '12px',
    fontFamily: 'monospace',
    wordBreak: 'break-all',
  },
  statusActive: {
    color: theme.success,
    fontWeight: '600',
    fontSize: '14px',
  },
  statusInactive: {
    color: theme.error,
    fontWeight: '600',
    fontSize: '14px',
  },
  infoRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    marginBottom: '12px',
  },
  resetButton: {
    width: '100%',
    padding: '12px',
    marginBottom: '12px',
    backgroundColor: 'transparent',
    border: `1px solid ${theme.warning}`,
    borderRadius: '8px',
    color: theme.warning,
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  },
  signOutButton: {
    width: '100%',
    padding: '12px',
    backgroundColor: 'transparent',
    border: `1px solid ${theme.error}`,
    borderRadius: '8px',
    color: theme.error,
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  },
  spinner: {
    width: '32px',
    height: '32px',
    border: `3px solid ${theme.border}`,
    borderTopColor: theme.accent,
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  // Empty state
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    gap: '8px',
  },
  emptyStateText: {
    color: theme.textSecondary,
    fontSize: '16px',
    margin: 0,
  },
  emptyStateSubtext: {
    color: theme.textSecondary,
    fontSize: '14px',
    margin: 0,
  },
  // Skeleton styles
  skeletonPost: {
    marginBottom: '20px',
  },
  skeletonUserRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px',
    padding: '0 4px',
  },
  skeletonUsername: {
    width: '120px',
    height: '18px',
    backgroundColor: theme.bgCard,
    borderRadius: '4px',
    animation: 'pulse 1.5s ease-in-out infinite',
  },
  skeletonFollowBtn: {
    width: '70px',
    height: '28px',
    backgroundColor: theme.bgCard,
    borderRadius: '14px',
    animation: 'pulse 1.5s ease-in-out infinite',
  },
  skeletonBanner: {
    width: '100px',
    height: '24px',
    backgroundColor: theme.bgCard,
    borderRadius: '4px 4px 0 0',
    marginBottom: '0',
    animation: 'pulse 1.5s ease-in-out infinite',
  },
  skeletonCard: {
    width: '100%',
    aspectRatio: '3/4',
    backgroundColor: theme.bgSheet,
    borderRadius: '0 12px 12px 12px',
    animation: 'pulse 1.5s ease-in-out infinite',
  },
  skeletonDetails: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '12px 0',
  },
  skeletonTimestamp: {
    width: '60px',
    height: '14px',
    backgroundColor: theme.bgCard,
    borderRadius: '4px',
    animation: 'pulse 1.5s ease-in-out infinite',
  },
  skeletonLevel: {
    width: '100px',
    height: '14px',
    backgroundColor: theme.bgCard,
    borderRadius: '4px',
    animation: 'pulse 1.5s ease-in-out infinite',
  },
  skeletonRating: {
    width: '100%',
    height: '40px',
    backgroundColor: theme.bgCard,
    borderRadius: '8px',
    animation: 'pulse 1.5s ease-in-out infinite',
  },
  // Post styles
  postContainer: {
    marginBottom: '24px',
  },
  userRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '10px',
    padding: '0 4px',
  },
  userInfoLeft: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  usernameButton: {
    background: 'none',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    textAlign: 'left',
  },
  username: {
    color: theme.text,
    fontSize: '15px',
    fontWeight: '600',
  },
  userMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    color: theme.textSecondary,
    fontSize: '12px',
  },
  metaDot: {
    width: '3px',
    height: '3px',
    borderRadius: '50%',
    backgroundColor: theme.textSecondary,
  },
  levelBadge: {
    padding: '2px 6px',
    borderRadius: '4px',
    color: '#ffffff',
    fontSize: '10px',
    fontWeight: '600',
  },
  distanceBadge: {
    color: theme.accent,
    fontSize: '11px',
    fontWeight: '500',
  },
  followButton: {
    padding: '6px 16px',
    borderRadius: '16px',
    border: `1px solid ${theme.accent}`,
    backgroundColor: 'transparent',
    color: theme.accent,
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  followingButton: {
    padding: '6px 16px',
    borderRadius: '16px',
    border: `1px solid ${theme.border}`,
    backgroundColor: theme.bgCard,
    color: theme.textSecondary,
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  menuWrapper: {
    position: 'relative',
  },
  moreButton: {
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    backgroundColor: 'transparent',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: theme.textSecondary,
    transition: 'all 0.15s ease',
  },
  heroContainer: {
    backgroundColor: theme.bgSheet,
    borderRadius: '12px',
    overflow: 'hidden',
    position: 'relative',
  },
  heroShine: {
    position: 'absolute',
    top: '-50%',
    left: '-50%',
    width: '200%',
    height: '200%',
    background: 'linear-gradient(120deg, transparent 30%, rgba(255,255,255,0.1) 48%, rgba(255,255,255,0.2) 50%, rgba(255,255,255,0.1) 52%, transparent 70%)',
    animation: 'heroShine 10s ease-in-out infinite',
    animationDelay: '1s',
    pointerEvents: 'none',
    zIndex: 1,
  },
  postTypeBanner: {
    backgroundColor: theme.bgCard,
    padding: '8px 12px',
    borderRadius: '8px 8px 0 0',
    textAlign: 'center',
  },
  postTypeText: {
    color: theme.textSecondary,
    fontSize: '12px',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  // Multi-card styles
  thumbnailGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))',
    gap: '10px',
    padding: '12px',
    backgroundColor: theme.bg,
  },
  thumbnailCard: {
    aspectRatio: '3/4',
    border: `2px solid ${theme.border}`,
    borderRadius: '8px',
    overflow: 'hidden',
    cursor: 'pointer',
    backgroundColor: theme.bgSheet,
    padding: 0,
    transition: 'all 0.2s ease',
  },
  thumbnailImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  thumbnailPlaceholder: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.bgCard,
  },
  thumbnailPlaceholderText: {
    color: theme.textSecondary,
    fontSize: '16px',
    fontWeight: '600',
  },
  multiCardHint: {
    textAlign: 'center',
    padding: '8px',
    backgroundColor: theme.bg,
  },
  multiCardHintText: {
    color: theme.textSecondary,
    fontSize: '12px',
  },
  backToGridBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '10px 16px',
    backgroundColor: theme.bgCard,
    border: 'none',
    color: theme.accent,
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  navArrowLeft: {
    position: 'absolute',
    left: '8px',
    top: '50%',
    transform: 'translateY(-50%)',
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: '#ffffff',
    zIndex: 10,
    transition: 'background-color 0.2s ease',
  },
  navArrowRight: {
    position: 'absolute',
    right: '8px',
    top: '50%',
    transform: 'translateY(-50%)',
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: '#ffffff',
    zIndex: 10,
    transition: 'background-color 0.2s ease',
  },
  paginationDots: {
    display: 'flex',
    justifyContent: 'center',
    gap: '8px',
    padding: '12px',
    backgroundColor: theme.bgSheet,
  },
  paginationDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    border: 'none',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
  },
  cardImageContainer: {
    position: 'relative',
    width: '100%',
    maxWidth: '280px',
    margin: '0 auto',
    aspectRatio: '3/4',
    backgroundColor: theme.bg,
    overflow: 'hidden',
    borderRadius: '14px',
  },
  cardImage: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    transition: 'opacity 0.3s ease',
    borderRadius: '14px',
  },
  cardImagePlaceholder: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.bgSheet,
  },
  cardImageLoading: {
    width: '40px',
    height: '40px',
    border: `3px solid ${theme.border}`,
    borderTopColor: theme.accent,
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  cardPlaceholderText: {
    color: theme.textSecondary,
    fontSize: '14px',
    textAlign: 'center',
    padding: '20px',
  },
  cardInfoOverlay: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    padding: '16px',
    background: 'linear-gradient(transparent, rgba(0,0,0,0.9))',
    textAlign: 'center',
  },
  cardName: {
    color: theme.text,
    fontSize: '16px',
    fontWeight: '600',
    marginBottom: '8px',
    display: 'block',
  },
  cardInfoRow: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap',
    justifyContent: 'center',
  },
  gradeChip: {
    padding: '4px 10px',
    borderRadius: '12px',
    backgroundColor: 'rgba(14, 165, 233, 0.2)',
    color: theme.accent,
    fontSize: '12px',
    fontWeight: '600',
  },
  tradingStatusChip: {
    padding: '4px 10px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '600',
  },
  postDetails: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 16px',
  },
  timestamp: {
    color: theme.textSecondary,
    fontSize: '13px',
  },
  levelIndicator: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  levelDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
  },
  levelText: {
    color: theme.textSecondary,
    fontSize: '12px',
  },
  // Action Row
  actionRow: {
    display: 'flex',
    justifyContent: 'center',
    gap: '16px',
    padding: '10px 16px',
    backgroundColor: theme.bgSheet,
    borderTop: `1px solid ${theme.borderSubtle}`,
  },
  actionLink: {
    background: 'none',
    border: 'none',
    color: theme.accent,
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
    padding: '4px 8px',
  },
  // Rating Row
  ratingRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 12px',
    marginTop: '8px',
  },
  tradeLink: {
    background: 'none',
    border: 'none',
    color: theme.error,
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
    padding: '4px 8px',
  },
  tradeLinkRequested: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    color: theme.error,
    fontSize: '12px',
    fontWeight: '600',
    padding: '4px 8px',
  },
  tradeLinkDisabled: {
    color: theme.textSecondary,
    fontSize: '12px',
    fontWeight: '500',
    padding: '4px 8px',
  },
  // Rating Ball System Styles
  ratingBallSystem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '42px', // Fixed height to prevent layout shift
  },
  ratingPromptContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '42px',
  },
  ratingPromptText: {
    color: theme.textSecondary,
    fontSize: '12px',
    fontWeight: '500',
  },
  ratingBallsContainer: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'center',
    alignItems: 'center',
    height: '42px',
  },
  ratingBallButton: {
    background: 'none',
    border: 'none',
    padding: '4px',
    cursor: 'pointer',
    position: 'relative',
    transition: 'transform 0.15s ease',
  },
  ratingBall: {
    width: '26px',
    height: '26px',
    borderRadius: '50%',
    position: 'relative',
    overflow: 'hidden',
    border: '2px solid theme.border',
    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.3)',
    transition: 'all 0.2s ease',
  },
  ratingBallSelected: {
    border: '2px solid #ffffff',
    boxShadow: '0 2px 12px rgba(255, 255, 255, 0.2)',
    transform: 'scale(1.1)',
  },
  ratingBallTop: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '50%',
    transition: 'background-color 0.2s ease',
  },
  ratingBallBottom: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '50%',
    backgroundColor: '#f5f5f5',
  },
  ratingBallBottomSelected: {
    backgroundColor: theme.text,
  },
  ratingBallDivider: {
    position: 'absolute',
    top: '50%',
    left: 0,
    right: 0,
    height: '3px',
    backgroundColor: theme.border,
    transform: 'translateY(-50%)',
    zIndex: 2,
  },
  ratingBallDividerSelected: {
    backgroundColor: theme.bgCard,
    height: '3px',
  },
  ratingCountBadge: {
    position: 'absolute',
    top: '-3px',
    right: '-3px',
    minWidth: '14px',
    height: '14px',
    borderRadius: '7px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 3px',
    zIndex: 10,
  },
  ratingCountText: {
    color: theme.text,
    fontSize: '9px',
    fontWeight: '700',
  },
});

export default Home;
